import { 
  getUserQuota, 
  incrementUsage,
  checkQuotaLimitWithData,
  createDefaultQuota,
  resetUserQuota
} from './firestoreApi';
import { UserQuota, PricingTier } from '../types';
import { UsageType, USAGE_TO_MAX_KEY_MAP } from './quotaConfig';

interface CachedQuota {
  data: UserQuota;
  timestamp: number;
  lastSync: number;
  pendingUpdates: Map<string, number>; // usageType -> pending increments
}

interface QuotaCacheState {
  userQuota: UserQuota | null;
  loading: boolean;
  error: string | null;
  lastSync: number;
}

// Cache storage
const quotaCache = new Map<string, CachedQuota>();

// Cache duration: 5 minutes (quotas change less frequently than subscriptions)
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Background sync interval: 1 minute
const BACKGROUND_SYNC_INTERVAL = 60 * 1000; // 1 minute

// Track background sync intervals per user
const backgroundSyncIntervals = new Map<string, NodeJS.Timeout>();

// Local quota state for each user
const localQuotaState = new Map<string, QuotaCacheState>();

/**
 * Check if user quotas should be reset based on monthly cycle
 */
const shouldResetQuotas = (lastReset: Date, resetPeriod: string): boolean => {
  if (resetPeriod === 'never') return false;
  
  const now = new Date();
  const lastResetDate = new Date(lastReset);
  
  // Check if it's been a full month (different month and year)
  const isDifferentMonth = now.getUTCMonth() !== lastResetDate.getUTCMonth();
  const isDifferentYear = now.getUTCFullYear() !== lastResetDate.getUTCFullYear();
  
  // Also check if it's been more than 30 days (for edge cases)
  const daysDiff = Math.floor((now.getTime() - lastResetDate.getTime()) / (1000 * 60 * 60 * 24));
  
  return (isDifferentMonth || isDifferentYear) && daysDiff >= 30;
};

/**
 * Initialize quota cache for a user
 */
export async function initializeQuotaCache(userId: string, subscriptionTier?: PricingTier): Promise<UserQuota> {
  try {
    // Fetch fresh quota data from cloud
    let userQuota = await getUserQuota(userId);
    
    if (!userQuota) {
      // Create default quota based on subscription tier
      const tier = subscriptionTier || PricingTier.Free;
      await createDefaultQuota(userId, tier);
      
      // Fetch the newly created quota
      userQuota = await getUserQuota(userId);
      
      if (!userQuota) {
        throw new Error('Failed to create and fetch default user quota');
      }
    }

    // Check if quotas should be reset (monthly reset for Free/Basic tiers)
    if (userQuota && shouldResetQuotas(userQuota.lastReset, userQuota.resetPeriod)) {
      console.log('🔄 Resetting quotas for user:', userId, {
        lastReset: userQuota.lastReset,
        currentDate: new Date(),
        tier: subscriptionTier || PricingTier.Free,
        resetPeriod: userQuota.resetPeriod
      });
      
      await resetUserQuota(userId, subscriptionTier || PricingTier.Free);
      userQuota = await getUserQuota(userId); // Fetch updated quota
      
      if (!userQuota) {
        throw new Error('Failed to fetch quota after reset');
      }
    }

    // Initialize local state
    const initialState: QuotaCacheState = {
      userQuota,
      loading: false,
      error: null,
      lastSync: Date.now()
    };
    
    localQuotaState.set(userId, initialState);

    // Cache the quota data
    quotaCache.set(userId, {
      data: userQuota,
      timestamp: Date.now(),
      lastSync: Date.now(),
      pendingUpdates: new Map()
    });

    // Start background sync
    startBackgroundSync(userId);

    return userQuota;
    
  } catch (error) {
    console.error('❌ [quotaCache] Failed to initialize quota cache:', error);
    throw error;
  }
}

/**
 * Get cached quota data (fast local access)
 */
export function getCachedQuota(userId: string): UserQuota | null {
  const cached = quotaCache.get(userId);
  if (!cached) return null;
  
  const now = Date.now();
  
  // Check if cache is still valid
  if (now - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  // Cache expired, mark for refresh
  return cached.data; // Return stale data for now
}

/**
 * Check quota limit using local data (fast)
 */
export async function checkQuotaLimitLocal(
  userId: string, 
  usageType: UsageType
): Promise<boolean> {
  const localState = localQuotaState.get(userId);
  if (!localState?.userQuota) {
    console.warn('⚠️ [quotaCache] No local quota state, falling back to cloud check');
    // Use the new function that returns both result and quota data
    const { hasExceeded, userQuota } = await checkQuotaLimitWithData(userId, usageType);
    // Update local cache with the quota data we just fetched
    if (userQuota) {
      updateLocalQuotaCache(userId, userQuota);
    }
    return hasExceeded;
  }

  const userQuota = localState.userQuota;
  
  const maxKey = USAGE_TO_MAX_KEY_MAP[usageType];
  if (!maxKey) {
    console.warn('⚠️ [quotaCache] Unknown usage type, falling back to cloud check');
    // Use the new function that returns both result and quota data
    const { hasExceeded, userQuota } = await checkQuotaLimitWithData(userId, usageType);
    // Update local cache with the quota data we just fetched
    if (userQuota) {
      updateLocalQuotaCache(userId, userQuota);
    }
    return hasExceeded;
  }

  const maxValue = userQuota[maxKey];
  
  if (maxValue === -1) return false; // Unlimited
  
  // Get current usage from user quota
  const currentUsage = userQuota[usageType];
  return currentUsage >= maxValue;
}

/**
 * Increment quota locally and queue cloud update
 */
export async function incrementQuotaLocal(
  userId: string,
  usageType: UsageType
): Promise<void> {
  const localState = localQuotaState.get(userId);
  if (!localState) {
    console.warn('⚠️ [quotaCache] No local quota state, updating cloud directly');
    await incrementUsage(userId, usageType);
    // After cloud update, refresh the local cache so subsequent calls use local state
    refreshQuotaCache(userId).catch(error => 
      console.warn('⚠️ [quotaCache] Failed to refresh cache after cloud increment:', error)
    );
    return;
  }

  // Update local state immediately
  if (localState.userQuota) {
    localState.userQuota[usageType]++;
  }

  // Queue cloud update
  const cached = quotaCache.get(userId);
  if (cached) {
    const currentPending = cached.pendingUpdates.get(usageType) || 0;
    cached.pendingUpdates.set(usageType, currentPending + 1);
  }


}

/**
 * Background sync: push local changes to cloud
 */
async function syncQuotasToCloud(userId: string): Promise<void> {
  const cached = quotaCache.get(userId);
  if (!cached || cached.pendingUpdates.size === 0) {
    return; // Nothing to sync
  }

  try {
    // Process all pending updates
    const pendingUpdatesArray = Array.from(cached.pendingUpdates.entries());
    for (const [usageType] of pendingUpdatesArray) {
      // Update cloud quota
      await incrementUsage(userId, usageType as any);
    }

    // Clear pending updates
    cached.pendingUpdates.clear();
    cached.lastSync = Date.now();
    
    // Update local state last sync time
    const localState = localQuotaState.get(userId);
    if (localState) {
      localState.lastSync = Date.now();
    }


    
  } catch (error) {
    console.error('❌ [quotaCache] Error syncing quotas to cloud:', error);
  }
}

/**
 * Start background sync for a user
 */
function startBackgroundSync(userId: string): void {
  // Clear existing interval if any
  const existingInterval = backgroundSyncIntervals.get(userId);
  if (existingInterval) {
    clearInterval(existingInterval);
  }

  // Start new background sync interval
  const interval = setInterval(() => {
    syncQuotasToCloud(userId);
  }, BACKGROUND_SYNC_INTERVAL);

  backgroundSyncIntervals.set(userId, interval);
}

/**
 * Stop background sync for a user
 */
export function stopBackgroundSync(userId: string): void {
  const interval = backgroundSyncIntervals.get(userId);
  if (interval) {
    clearInterval(interval);
    backgroundSyncIntervals.delete(userId);
  }
}

/**
 * Force sync all pending quota updates to cloud
 */
export async function forceQuotaSync(userId: string): Promise<void> {
  await syncQuotasToCloud(userId);
}

/**
 * Refresh quota cache from cloud
 */
export async function refreshQuotaCache(userId: string, subscriptionTier?: PricingTier): Promise<UserQuota> {
  try {
    let userQuota = await getUserQuota(userId);
    
    if (!userQuota && subscriptionTier) {
      // Create default quota based on subscription tier
      await createDefaultQuota(userId, subscriptionTier);
      
      // Fetch the newly created quota
      userQuota = await getUserQuota(userId);
      
      if (!userQuota) {
        throw new Error('Failed to create and fetch default user quota during refresh');
      }
    } else if (!userQuota) {
      throw new Error('Failed to fetch user quota from cloud and no subscription tier provided for default creation');
    }

    // Check if quotas should be reset (monthly reset for Free/Basic tiers)
    if (userQuota && shouldResetQuotas(userQuota.lastReset, userQuota.resetPeriod)) {
      console.log('🔄 Resetting quotas for user during refresh:', userId, {
        lastReset: userQuota.lastReset,
        currentDate: new Date(),
        tier: subscriptionTier || PricingTier.Free,
        resetPeriod: userQuota.resetPeriod
      });
      
      await resetUserQuota(userId, subscriptionTier || PricingTier.Free);
      userQuota = await getUserQuota(userId); // Fetch updated quota
      
      if (!userQuota) {
        throw new Error('Failed to fetch quota after reset during refresh');
      }
    }
    
    // Ensure local state exists and update it
    let localState = localQuotaState.get(userId);
    if (!localState) {
      localState = {
        userQuota: null,
        loading: false,
        error: null,
        lastSync: Date.now()
      };
      localQuotaState.set(userId, localState);
    }
    
    localState.userQuota = userQuota;
    localState.lastSync = Date.now();
    
    // Ensure cache exists and update it
    let cached = quotaCache.get(userId);
    if (!cached) {
      cached = {
        data: userQuota,
        timestamp: Date.now(),
        lastSync: Date.now(),
        pendingUpdates: new Map()
      };
      quotaCache.set(userId, cached);
      
      // Start background sync for new cache
      startBackgroundSync(userId);
    } else {
      cached.data = userQuota;
      cached.timestamp = Date.now();
      cached.lastSync = Date.now();
    }

    return userQuota;
    
  } catch (error) {
    console.error('❌ [quotaCache] Failed to refresh quota cache:', error);
    throw error;
  }
}

/**
 * Clear quota cache for a specific user
 */
export function clearUserQuotaCache(userId: string): void {
  quotaCache.delete(userId);
  localQuotaState.delete(userId);
  stopBackgroundSync(userId);
}

/**
 * Get quota cache status for debugging
 */
export function getQuotaCacheStatus(userId: string): any {
  const cached = quotaCache.get(userId);
  const localState = localQuotaState.get(userId);
  
  if (!cached && !localState) {
    return { status: 'no_cache' };
  }
  
  return {
    status: 'cached',
    cacheAge: cached ? Date.now() - cached.timestamp : null,
    lastSync: cached?.lastSync || localState?.lastSync || null,
    pendingUpdates: cached ? Array.from(cached.pendingUpdates.entries()) : [],
    localState: localState ? {
      hasQuota: !!localState.userQuota,
      lastSync: localState.lastSync,
      error: localState.error
    } : null
  };
}

/**
 * Update local quota cache with data from cloud (without making another API call)
 * This is useful when we already have quota data from a cloud check
 */
export function updateLocalQuotaCache(userId: string, userQuota: UserQuota): void {
  // Ensure local state exists and update it
  let localState = localQuotaState.get(userId);
  if (!localState) {
    localState = {
      userQuota: null,
      loading: false,
      error: null,
      lastSync: Date.now()
    };
    localQuotaState.set(userId, localState);
  }
  
  localState.userQuota = userQuota;
  localState.lastSync = Date.now();
  
  // Ensure cache exists and update it
  let cached = quotaCache.get(userId);
  if (!cached) {
    cached = {
      data: userQuota,
      timestamp: Date.now(),
      lastSync: Date.now(),
      pendingUpdates: new Map()
    };
    quotaCache.set(userId, cached);
    
    // Start background sync for new cache
    startBackgroundSync(userId);
  } else {
    cached.data = userQuota;
    cached.timestamp = Date.now();
    cached.lastSync = Date.now();
  }
  
}

/**
 * Clear all quota caches (useful for logout)
 */
export function clearAllQuotaCaches(): void {
  // Clear all user quota caches
  const userIds = Array.from(quotaCache.keys());
  for (const userId of userIds) {
    clearUserQuotaCache(userId);
  }
  
  // Clear background sync intervals
  const intervals = Array.from(backgroundSyncIntervals.values());
  for (const interval of intervals) {
    clearInterval(interval);
  }
  backgroundSyncIntervals.clear();
  
}

/**
 * Update user quotas when subscription tier changes
 */
export async function updateQuotasForSubscriptionChange(
  userId: string, 
  newTier: PricingTier
): Promise<UserQuota> {
  try {
    // Import the resetUserQuota function dynamically to avoid circular dependencies
    const { resetUserQuota } = await import('./firestoreApi');
    
    // Reset user quotas for the new tier (this will update limits and reset usage to 0)
    await resetUserQuota(userId, newTier);
    
    // Fetch the updated quota
    const userQuota = await getUserQuota(userId);
    
    if (!userQuota) {
      throw new Error('Failed to fetch updated user quota after subscription change');
    }
    
    // Update local state
    const localState = localQuotaState.get(userId);
    if (localState) {
      localState.userQuota = userQuota;
      localState.lastSync = Date.now();
    }
    
    // Update cache
    const cached = quotaCache.get(userId);
    if (cached) {
      cached.data = userQuota;
      cached.timestamp = Date.now();
      cached.lastSync = Date.now();
      // Clear any pending updates since we just reset the quotas
      cached.pendingUpdates.clear();
    }

    return userQuota;
    
  } catch (error) {
    console.error('❌ [quotaCache] Failed to update quotas for subscription change:', error);
    throw error;
  }
}
